import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'

import './useCopyFeedback.module.scss'

const CONFIRM_DURATION_MS = 2000
const COPIED_MESSAGE = { id: 'UIC.GENERAL.COPIED', defaultMessage: 'Copied' }

// Fallback for contexts where the async Clipboard API is unavailable or blocked (non-secure origins,
// permission-denied iframes, older browsers). Returns whether the copy actually succeeded.
const legacyCopy = (value) => {
  if (typeof document === 'undefined') {
    return false
  }

  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)

  try {
    textarea.select()
    return document.execCommand('copy')
  } catch (error) {
    return false
  } finally {
    textarea.remove()
  }
}

const writeToClipboard = async (value) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch (error) {
    // fall through to the legacy path below
  }

  return legacyCopy(value)
}

/**
 * Shared copy-to-clipboard feedback used by every copy affordance. `copy(value, event?)` writes to the
 * clipboard (Clipboard API with an execCommand fallback) and, ONLY on a successful write, shows a
 * screen-level "Copied" toast (bottom-center, portaled to <body>) for a couple of seconds. Keeping the
 * write + toast here means every copy button behaves identically and never shows a false confirmation.
 *
 * Returns `copied` (confirmation state), `copy` (the click handler), and `toast` (the node to render).
 */
export default function useCopyFeedback() {
  const intl = useIntl()
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef(null)

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  const copy = useCallback(async (value, event) => {
    event?.stopPropagation()
    // Never write an empty/nullish value (it would copy "" or the string "null") or show a false
    // "Copied" confirmation — consumers that pass through an absent field rely on this guard.
    if (value == null || value === '') {
      return
    }
    const succeeded = await writeToClipboard(value)
    if (!succeeded) {
      return
    }
    setCopied(true)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => setCopied(false), CONFIRM_DURATION_MS)
  }, [])

  const toast =
    copied && typeof document !== 'undefined'
      ? createPortal(
          <div className="Uic-CopyToast" role="status" aria-live="polite">
            {intl.formatMessage(COPIED_MESSAGE)}
          </div>,
          document.body,
        )
      : null

  return { copied, copy, toast }
}
